import { toggleSelectNoneClass } from 'components/editor/helpers/toggleSelectNoneClass';
import type { ResizeCallback } from 're-resizable';
import { Resizable } from 're-resizable';
import type { MouseEvent } from 'react';
import type React from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { DraggableData, DraggableEvent } from 'react-draggable';
import { DraggableCore } from 'react-draggable';
import { useSelector } from 'react-redux';

import { SIZES } from '../../../../muiTheme/MuiDataGrid';
import type { RootState } from '../../grid/reduxStore/Store';
import { selectBlockStyleById } from '../../grid/reduxStore/blockStyleSettingsSlice';
import { buildBlockTransform, normalizeRotation } from '../helpers/rotation';
import { useSectionData } from '../../Sections/useSectionContext';
import { GridBlockType } from 'services/entities/BlockTypes';
import { gridPixelSize } from '../../shared/gridConfig';
import PlaceHolder from '../Block/Placeholder';
import { BlockSettingsToolsContainer } from '../BlockSettings/BlockSettingsToolsContainer';
import { useOpenBlockSettings } from '../BlockSettings/useOpenBlockSettings';
import ContentClipper from '../Clipper/ContentClipper';
import { availableResizeHandle } from '../consts';
import type { CustomContextMenuOptions } from '../CustomContextMenu/CustomContextMenuContainer.tsx';
import { CustomContextMenuContainer } from '../CustomContextMenu/CustomContextMenuContainer.tsx';
import { useBlockClipboardOptions } from '../CustomContextMenu/useBlockClipboardOptions/index.tsx';
import { useBlockLockContextMenuOption } from '../CustomContextMenu/useBlockLockContextMenuOption.tsx';
import { SelectionContext } from '../SelectedBlockInfoProvider';
import { useInViewport } from '../useInViewport';
import type { BlockDimensionType, BlockPositionType, DraggableGridBlockWrapperProps } from './types';
import useBlockLayer from './useBlockLayer';
import { useDraggableBlockManipulations } from './useDraggableBlockManipulations';
import { useBlockKeyboardShortcuts } from './useBlockKeyboardShortcuts';
import { withBlockLockCheck } from '../Block/withBlockLockCheck.tsx';
import { useSignaturesContext } from 'components/editor/providers/SignaturesProvider.tsx';
import { useComments } from 'providers/CommentsContext';
import { MIN_COLUMN_WIDTH } from '../Block/Table/defaultData';

const DraggableGridBlockWrapperBase: React.FC<DraggableGridBlockWrapperProps> = ({
  blockId,
  children,
  onMouseDown,
  isEditMode = false,
  setIsEditMode,
  lockAspectRatio = false,
  sumBorderWidth,
  disableResizingHandlers = false,
  containerRef,
  locked = false,
  blockConfig: propsBlockConfig,
  blockType: propsBlockType,
  elementToBound,
  onBlockResizeStart,
  onBlockResize,
  onBlockResizeStop,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const {
    handleOnResizing,
    handleDrag,
    handleDragStart,
    handleOnResizeStop: handleResizeStop,
    handleStop,
  } = useDraggableBlockManipulations();

  const { bringGridToFront, bringGridForward, sendGridBackward, sendGridToBack } = useBlockLayer();
  const { blocksContent, getSectionBlocksLayer, sectionId } = useSectionData();
  const { blockConfig, type: blockType } = blocksContent[blockId] || {
    blockConfig: propsBlockConfig,
    type: propsBlockType,
  };
  const { scrollToPosition } = useInViewport();
  const positionOnInteractionStart = useRef<BlockPositionType>({ xAxisPx: 0, yAxisPx: 0 });
  const dimensionOnInteractionStart = useRef<BlockDimensionType>({ heightPx: 0, widthPx: 0 });
  const wasDraggedRef = useRef(false);
  const wasAlreadySelectedRef = useRef(false);
  const nodeRef = useRef<HTMLElement | null>(null);
  const { selectedBlockIdByWrapper, handleSelectedBlockInfoByWrapper } = useContext(SelectionContext);
  const { selectedSignature, setSelectedSignature } = useSignaturesContext();
  const { commentsEnabled } = useComments();
  const isSelectedState = blockId === selectedBlockIdByWrapper || blockId === selectedSignature?.signatureId;
  const isContentClipperActive = (isSelectedState || isEditMode) && !isDragging && !locked;
  const isLockedWithComments = locked || commentsEnabled;
  const isMac = navigator.userAgent.includes('Mac');
  const isTableInEditMode = isEditMode && blockType === GridBlockType.TABLE && containerRef;
  const { openBlockSettings, openImageLibraryPanel, closeBlockSettings } = useOpenBlockSettings();
  const copyAndPasteMenuOptions = useBlockClipboardOptions(locked);
  const lockContextMenuOption = useBlockLockContextMenuOption({ blockId, blockType, isEditMode, setIsEditMode });
  const rotation = normalizeRotation(useSelector((state: RootState) => selectBlockStyleById(state, blockId))?.rotation);

  useBlockKeyboardShortcuts(blockId);

  const positionValueDelta = isTableInEditMode ? SIZES.columnHeaderHeight * -1 : 0;
  const reorderColumnWidth = isTableInEditMode ? SIZES.reorderColumnWidth * -1 : 0;

  const mapAvailableResizeHandle = () =>
    Object.keys(availableResizeHandle).reduce((resizeHandles, key) => {
      resizeHandles[key] = true;
      return resizeHandles;
    }, {});

  // In edit mode, blockConfig stores data-column-only dimensions (no reorder column
  // or column-header chrome). The Resizable must visually cover the full rendered
  // DataGrid including that chrome, so we add the measured values back here.
  // These are visual-only — blockConfig.width/height are never modified here.
  const blockSize = {
    width: blockConfig.width - reorderColumnWidth,
    height: blockConfig.height - positionValueDelta,
  };

  const currentBlock = {
    x: positionOnInteractionStart.current.xAxisPx,
    y: positionOnInteractionStart.current.yAxisPx,
    width: blockConfig.width,
    height: blockConfig.height,
  };

  const getResizingSides = disableResizingHandlers ? false : mapAvailableResizeHandle();

  const blockLevelMenuOptions: CustomContextMenuOptions = [
    {
      type: 'item',
      title: 'Bring to front',
      callback: () => bringGridToFront(blockConfig, getSectionBlocksLayer().greaterZIndexAvailable),
      disabled: locked,
    },
    {
      type: 'item',
      title: 'Bring forward',
      callback: () => bringGridForward(blockConfig, blocksContent),
      disabled: locked,
    },
    {
      type: 'item',
      title: 'Send backward',
      callback: () => sendGridBackward(blockConfig, blocksContent),
      disabled: locked,
    },
    {
      type: 'item',
      title: 'Send to back',
      callback: () => sendGridToBack(blockConfig, getSectionBlocksLayer().lowerZIndexAvailable),
      disabled: locked,
    },
  ];

  const customContextMenuOptions: CustomContextMenuOptions =
    blockType === GridBlockType.SIGNATURE
      ? [...copyAndPasteMenuOptions]
      : [...copyAndPasteMenuOptions, { type: 'divider' }, lockContextMenuOption, { type: 'divider' }, ...blockLevelMenuOptions];

  const customStyle = useMemo<React.CSSProperties>(() => {
    return {
      position: 'absolute',
      top: 0,
      left: 0,
      transform: buildBlockTransform(blockConfig.x + reorderColumnWidth, blockConfig.y + positionValueDelta, rotation),
    };
  }, [blockConfig.x, blockConfig.y, positionValueDelta, reorderColumnWidth, onBlockResizeStop, rotation]);

  const onStartHandle = () => {
    positionOnInteractionStart.current = { xAxisPx: blockConfig.x, yAxisPx: blockConfig.y };
    wasDraggedRef.current = false;
    handleDragStart();
  };

  const onDragHandle = useCallback(
    (e: DraggableEvent, data: DraggableData) => {
      setIsDragging(true);
      wasDraggedRef.current = true;
      scrollToPosition(data.y);
      handleDrag(e as MouseEvent, data, { ...blockConfig }, blockType, elementToBound);
    },
    [blockConfig.x, blockConfig.y, blockConfig.height, blockConfig.width, elementToBound]
  );

  const onStopHandle = (event: DraggableEvent) => {
    setIsDragging(false);
    handleStop(event as MouseEvent, positionOnInteractionStart.current, blockConfig, sectionId, blockType);
    if (!wasDraggedRef.current && wasAlreadySelectedRef.current && !isEditMode) {
      setIsEditMode?.(true);
    }
  };

  const onMouseDownHandle = (e) => {
    e.stopPropagation();
    wasAlreadySelectedRef.current = selectedBlockIdByWrapper === blockId;

    if (locked) {
      closeBlockSettings();
      if (blockType === GridBlockType.SIGNATURE) {
        handleSelectedBlockInfoByWrapper({
          blockId: null,
          sectionId: null,
        });
        setSelectedSignature({
          signatureId: blockId,
          sectionId,
        });
      } else {
        setSelectedSignature({
          signatureId: null,
          sectionId: null,
        });
        handleSelectedBlockInfoByWrapper({
          blockId,
          sectionId,
        });
      }

      return;
    }

    if (blockType === GridBlockType.IMAGE && !blocksContent[blockId].content) {
      openImageLibraryPanel(blockId);
    }
    if (blockType !== GridBlockType.TABLE && blockType !== GridBlockType.SIGNATURE) {
      openBlockSettings(blockType, blockId);
    }
    onMouseDown?.(e);
  };

  const onResizeStartHandle = (event) => {
    positionOnInteractionStart.current = { xAxisPx: blockConfig.x, yAxisPx: blockConfig.y };
    dimensionOnInteractionStart.current = {
      heightPx: blockConfig.height,
      widthPx: blockConfig.width,
    };
    event.stopPropagation();
    toggleSelectNoneClass(true);
    onBlockResizeStart?.();
  };

  const onResize = (_event, direction, _refToElement, delta) => {
    handleOnResizing(direction, delta, positionOnInteractionStart.current, dimensionOnInteractionStart.current, blockConfig, blockType);
    onBlockResize?.(dimensionOnInteractionStart.current.widthPx + delta.width);
  };

  const onResizeStop: ResizeCallback = (_event, direction, _elementRef, delta) => {
    const newBlockConfig = { ...blockConfig };
    if (containerRef?.current?.offsetHeight) {
      newBlockConfig.height = containerRef?.current?.offsetHeight;
    }
    handleResizeStop(
      direction,
      delta,
      newBlockConfig,
      positionOnInteractionStart.current,
      dimensionOnInteractionStart.current,
      sumBorderWidth,
      blockType,
      onBlockResizeStop
    );
    toggleSelectNoneClass(false);
  };

  const blockClassName = `block_${blockId} block-type-${blockType.toLowerCase()} editor__page__draggable ${isMac ? 'is_mac' : ''} ${
    isEditMode ? 'edit-state' : ''
  } ${isSelectedState ? 'selected-state' : ''} ${locked ? 'locked-state' : ''}`;

  const tableMinWidth = useMemo(() => {
    if (blockType !== GridBlockType.TABLE) return gridPixelSize;
    const tableContent = blocksContent[blockId]?.contentTable;
    if (!tableContent?.columns) return gridPixelSize;
    return (tableContent.columns.length - 1) * MIN_COLUMN_WIDTH;
  }, [blockType, blockId, blocksContent]);

  // When lockAspectRatio is enabled, pass the exact stored ratio (width/height) to
  // re-resizable instead of `true`. re-resizable computes the ratio from the DOM element's
  // clientWidth/clientHeight when given `true`, but sub-pixel heights (e.g. 0.75px) are
  // rounded to 1px by browsers, which corrupts the ratio. Passing the numeric ratio from
  // the Redux blockConfig bypasses the DOM-based calculation and preserves accuracy.
  // This wrapper is shared by Image, Video, Shape and Signature blocks — the fix applies
  // to all of them intentionally, as any block type can suffer the same sub-pixel rounding.
  const lockAspectRatioValue: boolean | number = useMemo(() => {
    if (!lockAspectRatio) return false;
    const { width, height } = blockConfig;
    if (!width || width <= 0 || !height || height <= 0) return true;
    return width / height;
  }, [lockAspectRatio, blockConfig.width, blockConfig.height]);

  return (
    <CustomContextMenuContainer
      options={customContextMenuOptions}
      testId={`${blockType.toLowerCase()}-block`}
      isEnabled={!isEditMode && !commentsEnabled}
    >
      <div className={`${blockType.toLowerCase()}-block-stacking`} style={{ zIndex: blockConfig.z, position: 'relative' }}>
        <DraggableCore
          nodeRef={nodeRef}
          onStart={onStartHandle}
          onDrag={onDragHandle}
          onStop={onStopHandle}
          scale={1}
          onMouseDown={onMouseDownHandle}
          grid={[gridPixelSize, gridPixelSize]}
          disabled={isEditMode || isLockedWithComments}
        >
          <Resizable
            ref={(instance) => {
              // Only set on mount, never null out on unmount: DraggableCore's own unmount cleanup
              // needs a valid nodeRef to remove its global drag listeners, and children unmount
              // before parents, so nulling here would race ahead of that cleanup.
              if (instance) {
                nodeRef.current = instance.resizable ?? null;
              }
            }}
            className={blockClassName}
            size={blockSize}
            scale={1}
            data-testid={'resizable-block-wrapper'}
            minWidth={tableMinWidth}
            style={customStyle}
            minHeight={gridPixelSize}
            grid={[gridPixelSize, gridPixelSize]}
            handleComponent={availableResizeHandle}
            enable={isLockedWithComments || !isSelectedState || isEditMode || rotation !== 0 ? false : getResizingSides}
            onResizeStart={onResizeStartHandle}
            onResize={onResize}
            onResizeStop={onResizeStop}
            lockAspectRatio={lockAspectRatioValue}
            bounds={elementToBound}
          >
            {isSelectedState && (
              <BlockSettingsToolsContainer blockId={blockId} isEditMode={isEditMode} setIsEditMode={setIsEditMode} rotation={rotation} />
            )}
            {children}
            {!isContentClipperActive && blockType !== GridBlockType.SIGNATURE && <ContentClipper blockId={blockId} />}
          </Resizable>
        </DraggableCore>
      </div>
      {isDragging && <PlaceHolder trackedBlocks={currentBlock} />}
    </CustomContextMenuContainer>
  );
};

DraggableGridBlockWrapperBase.displayName = 'DraggableGridBlockWrapper';

export const DraggableGridBlockWrapper = withBlockLockCheck<DraggableGridBlockWrapperProps>(DraggableGridBlockWrapperBase);
